import React, { useEffect, useRef, useState } from "react";
import {
  FaBirthdayCake,
  FaCheck,
  FaEnvelope,
  FaExchangeAlt,
  FaFlag,
  FaIdCard,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaTimes,
  FaUser,
  FaVenusMars,
  FaWeight,
} from "react-icons/fa";

import {
  buildAdherentEditForm,
  GENDER_OPTIONS,
  getAdherentFullName,
  getGroupColorStyle,
  validateAdherentForm,
} from "../utils/groupsUtils";

function FieldLabel({ children }) {
  return (
    <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
      {children}
    </span>
  );
}

function TextInput({
  icon: Icon,
  label,
  name,
  value,
  onChange,
  placeholder,
  type = "text",
  required = false,
  inputRef,
  autoComplete,
  maxLength,
  min,
  step,
}) {
  return (
    <label className="block">
      <FieldLabel>{label}</FieldLabel>

      <div className="flex h-12 w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-300 focus-within:ring-4 focus-within:ring-sky-100 dark:border-white/10 dark:bg-slate-950 dark:focus-within:border-sky-300/50 dark:focus-within:ring-sky-500/10">
        {Icon && (
          <Icon className="shrink-0 text-slate-400 dark:text-white/35" />
        )}

        <input
          ref={inputRef}
          type={type}
          name={name}
          value={value ?? ""}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          autoComplete={autoComplete}
          maxLength={maxLength}
          min={min}
          step={step}
          className="min-w-0 flex-1 border-0 bg-transparent py-3 text-sm font-bold text-slate-700 outline-none placeholder:text-slate-400 focus:outline-none focus:ring-0 dark:text-white dark:placeholder:text-white/30"
        />
      </div>
    </label>
  );
}

function AdherentEditDrawer({
  isOpen,
  adherent,
  group,
  groups = [],
  saving,
  onClose,
  onSubmit,
}) {
  const firstInputRef = useRef(null);

  const [form, setForm] = useState(
    buildAdherentEditForm(adherent, group?.id),
  );

  const [localError, setLocalError] = useState("");

  const colorStyle = getGroupColorStyle(group?.color_groupe);

  useEffect(() => {
    if (!isOpen) return;

    setForm(buildAdherentEditForm(adherent, group?.id));
    setLocalError("");

    setTimeout(() => {
      firstInputRef.current?.focus();
    }, 80);

    const handleKeyDown = (keyboardEvent) => {
      if (keyboardEvent.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, adherent, group, onClose]);

  if (!isOpen) return null;

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (localError) {
      setLocalError("");
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const error = validateAdherentForm(form);

    if (error) {
      setLocalError(error);
      return;
    }

    onSubmit(form);
  };

  const currentGroupId = String(group?.id || "");
  const targetGroupId = String(form.target_group_id || "");

  const canMove = groups.length > 1;

  const willMove =
    canMove &&
    currentGroupId &&
    targetGroupId &&
    currentGroupId !== targetGroupId;

  return (
    <div className="fixed inset-0 z-[90] flex justify-end bg-slate-950/70 backdrop-blur-sm">
      <div
        className="h-full w-full max-w-2xl bg-white shadow-2xl dark:bg-slate-900"
        role="dialog"
        aria-modal="true"
        aria-label="Modifier le judoka"
      >
        <div className="flex h-full flex-col">
          <div
            className={`bg-gradient-to-br ${colorStyle.header} p-5 text-white`}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/65">
                  Modification judoka
                </p>

                <h2 className="mt-2 font-titre text-2xl font-bold">
                  {getAdherentFullName(adherent)}
                </h2>

                <p className="mt-2 text-sm font-medium text-white/75">
                  Groupe actuel : {group?.nom || "Non renseigné"}
                </p>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur-xl transition hover:bg-white/25"
                aria-label="Fermer"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex flex-1 flex-col overflow-hidden"
          >
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <div className="rounded-[1.5rem] border border-sky-200 bg-sky-50 p-4 text-sm font-semibold leading-7 text-sky-800 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-200">
                Tu peux modifier les informations du judoka. Si tu gères
                plusieurs groupes, tu peux aussi le déplacer vers un autre
                groupe.
              </div>

              {willMove && (
                <div className="mt-4 rounded-[1.5rem] border border-orange-200 bg-orange-50 p-4 text-sm font-bold leading-7 text-orange-800 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-200">
                  <FaExchangeAlt className="mr-2 inline-block" />
                  Ce judoka sera déplacé vers un autre groupe lors de
                  l’enregistrement.
                </div>
              )}

              {localError && (
                <div
                  className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300"
                  role="alert"
                >
                  {localError}
                </div>
              )}

              <div className="mt-5">
                <label className="block">
                  <FieldLabel>Groupe</FieldLabel>

                  <div className="relative">
                    <FaExchangeAlt className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

                    <select
                      name="target_group_id"
                      value={form.target_group_id}
                      onChange={handleChange}
                      disabled={!canMove}
                      className="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-sm font-bold text-slate-700 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:disabled:bg-white/5 dark:disabled:text-white/30 dark:focus:border-sky-300/50 dark:focus:ring-sky-500/10"
                    >
                      {groups.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.nom}
                        </option>
                      ))}
                    </select>
                  </div>
                </label>
              </div>

              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <TextInput
                  inputRef={firstInputRef}
                  icon={FaUser}
                  label="Nom *"
                  name="nom"
                  value={form.nom}
                  onChange={handleChange}
                  placeholder="Ex : DUPONT"
                  autoComplete="family-name"
                  required
                />

                <TextInput
                  icon={FaUser}
                  label="Prénom *"
                  name="prenom"
                  value={form.prenom}
                  onChange={handleChange}
                  placeholder="Ex : Lucas"
                  autoComplete="given-name"
                  required
                />
              </div>

              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <label className="block">
                  <FieldLabel>Genre</FieldLabel>

                  <div className="relative">
                    <FaVenusMars className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

                    <select
                      name="genre"
                      value={form.genre}
                      onChange={handleChange}
                      className="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-sm font-bold text-slate-700 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-sky-300/50 dark:focus:ring-sky-500/10"
                    >
                      {GENDER_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </label>

                <TextInput
                  icon={FaBirthdayCake}
                  label="Date de naissance"
                  name="date_naissance"
                  value={form.date_naissance}
                  onChange={handleChange}
                  type="date"
                />
              </div>

              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <TextInput
                  icon={FaWeight}
                  label="Poids"
                  name="poids"
                  value={form.poids}
                  onChange={handleChange}
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="Ex : 38"
                />

                <TextInput
                  icon={FaIdCard}
                  label="Numéro de licence"
                  name="licence_number"
                  value={form.licence_number}
                  onChange={handleChange}
                  placeholder="Ex : 123456"
                />
              </div>

              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <TextInput
                  icon={FaMapMarkerAlt}
                  label="Club"
                  name="club"
                  value={form.club}
                  onChange={handleChange}
                  placeholder="Ex : U.S. Bouscat Judo"
                />

                <TextInput
                  icon={FaMapMarkerAlt}
                  label="Département"
                  name="departement"
                  value={form.departement}
                  onChange={handleChange}
                  placeholder="Ex : Gironde"
                />
              </div>

              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <TextInput
                  icon={FaFlag}
                  label="Pays"
                  name="pays"
                  value={form.pays}
                  onChange={handleChange}
                  placeholder="France"
                />

                <TextInput
                  icon={FaIdCard}
                  label="URL licence"
                  name="licence_url"
                  value={form.licence_url}
                  onChange={handleChange}
                  type="url"
                  placeholder="https://..."
                />
              </div>

              <div className="mt-5">
                <TextInput
                  icon={FaUser}
                  label="URL photo"
                  name="photo_url"
                  value={form.photo_url}
                  onChange={handleChange}
                  placeholder="/img/avatar/avatarM001.webp ou https://..."
                />
              </div>

              <section className="mt-6 overflow-hidden rounded-[1.75rem] border border-red-200 bg-red-50/70 dark:border-red-500/20 dark:bg-red-500/[0.07]">
                <div className="border-b border-red-200/70 p-4 dark:border-red-500/15 sm:p-5">
                  <h3 className="font-titre text-lg font-bold text-slate-950 dark:text-white">
                    Coordonnées de contact
                  </h3>

                  <p className="mt-1 text-sm font-medium leading-6 text-slate-600 dark:text-white/55">
                    Ces coordonnées seront accessibles au coach dans la fiche
                    du judoka pour un appel ou un e-mail rapide.
                  </p>
                </div>

                <div className="grid gap-5 p-4 sm:grid-cols-2 sm:p-5">
                  <TextInput
                    icon={FaPhoneAlt}
                    label="Téléphone"
                    name="tel_adherent"
                    value={form.tel_adherent}
                    onChange={handleChange}
                    type="tel"
                    autoComplete="tel"
                    maxLength={30}
                    placeholder="06 12 34 56 78"
                  />

                  <TextInput
                    icon={FaEnvelope}
                    label="Adresse e-mail"
                    name="mail_adherent"
                    value={form.mail_adherent}
                    onChange={handleChange}
                    type="email"
                    autoComplete="email"
                    maxLength={255}
                    placeholder="contact@exemple.fr"
                  />
                </div>
              </section>
            </div>

            <div className="sticky bottom-0 grid gap-2 border-t border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900 sm:grid-cols-2">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-white/[0.05] dark:text-white"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={saving}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-sky-600 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500"
              >
                <FaCheck />
                {saving ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AdherentEditDrawer;